/**
 * The broadcast mix: what the platform is playing, plus the host talking.
 *
 * ## `createMediaElementSource` is a one-way door
 *
 * It may be called **once per media element, for the lifetime of the page**. A
 * second call throws `InvalidStateError`, which in a studio means: open it,
 * close it, open it again, and the second session has no music. It also
 * *rewires* the element - once tapped, the audio no longer reaches the speakers
 * on its own and has to be reconnected explicitly, or the host's own playback
 * goes silent the moment they open the studio.
 *
 * Both facts are why this lives in a module rather than in a hook. The tap is
 * cached against the element, the reconnection happens in the same breath as
 * the tap, and no component can get either wrong because no component does
 * either.
 *
 * ## Why tap the deck at all
 *
 * Because the alternative is the host playing music into a microphone, or the
 * platform giving up on knowing what is playing. Mixing the deck here keeps the
 * catalogue in charge of playback - which keeps play counts, `station_history`,
 * discovery credit and royalty accounting alive during a video broadcast. That
 * is the whole argument for a browser studio over an OBS ingest, and this
 * function is where it is cashed in.
 */

let context: AudioContext | null = null;

/** One context for the page. Several would each carry their own clock drift. */
export function audioContext(): AudioContext {
  context ??= new AudioContext();

  return context;
}

/**
 * Element taps, cached forever.
 *
 * A `WeakMap` so an element that is discarded takes its node with it, and a
 * cache rather than a flag because the node itself is what a second caller
 * needs - re-tapping is impossible, so sharing is the only option.
 */
const taps = new WeakMap<HTMLMediaElement, MediaElementAudioSourceNode>();

/**
 * The deck's output as a node, tapping it if nobody has yet.
 *
 * Returns null when there is no player on the page at all, which is a real
 * state - the studio can be opened before anything has been played - and not an
 * error. The mix simply has no music in it until there is.
 */
export function deckSource(): MediaElementAudioSourceNode | null {
  const element = document.querySelector('audio');

  if (!element) return null;

  const existing = taps.get(element);

  if (existing) return existing;

  const ctx = audioContext();
  const source = ctx.createMediaElementSource(element);

  // Immediately, in the same statement sequence as the tap. Between these two
  // lines the host's own playback is silent, and anything that could interleave
  // there - an await, an early return, a throw - leaves it that way.
  source.connect(ctx.destination);

  taps.set(element, source);

  return source;
}

export interface Mix {
  /** What gets published. */
  stream: MediaStream;
  /** 0..1, applied to the music. */
  setDeckLevel(level: number): void;
  /** 0..1, applied to the microphone. */
  setMicLevel(level: number): void;
  /** Peak amplitude right now, for a level meter. */
  peak(): number;
  stop(): void;
}

/**
 * Build the mix. `mic` may be null for a broadcast with no voice on it.
 */
export function createMix(mic: MediaStream | null): Mix {
  const ctx = audioContext();
  const destination = ctx.createMediaStreamDestination();

  const deckGain = ctx.createGain();
  const micGain = ctx.createGain();

  deckGain.gain.value = 0.8;
  micGain.gain.value = 1;

  /*
   * The meter sits *in* the path rather than tapped off the side of it.
   *
   * An `AnalyserNode` whose output goes nowhere is not pulled by the rendering
   * graph, so it reports silence however loud its input is - a meter that
   * always reads zero, which is worse than no meter because the host believes
   * it. Putting it inline fixes that and makes it measure the exact signal
   * being published rather than an approximation of it.
   */
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  analyser.connect(destination);

  const deck = deckSource();

  deck?.connect(deckGain);
  deckGain.connect(analyser);

  let micSource: MediaStreamAudioSourceNode | null = null;

  if (mic && mic.getAudioTracks().length > 0) {
    micSource = ctx.createMediaStreamSource(mic);
    micSource.connect(micGain);
    micGain.connect(analyser);
    /*
     * Note what is missing: `micGain.connect(ctx.destination)`.
     *
     * The microphone is never routed to the speakers. A host on a laptop with
     * no headphones would otherwise have their own voice come out of the
     * speaker their microphone is pointed at, and the resulting feedback howl
     * goes out to everybody listening.
     */
  }

  const samples = new Float32Array(analyser.fftSize);

  return {
    stream: destination.stream,
    setDeckLevel: (level) => {
      deckGain.gain.value = clamp(level);
    },
    setMicLevel: (level) => {
      micGain.gain.value = clamp(level);
    },
    peak: () => {
      analyser.getFloatTimeDomainData(samples);

      let peak = 0;

      for (const sample of samples) peak = Math.max(peak, Math.abs(sample));

      return peak;
    },
    stop: () => {
      /*
       * The gain nodes are disconnected; `deck` is not.
       *
       * It cannot be re-created, so it has to survive for the next session -
       * and its connection to `ctx.destination` is what keeps the host's own
       * playback audible after the studio closes. Disconnecting it here would
       * silence the site until a reload.
       */
      deck?.disconnect(deckGain);
      deckGain.disconnect();
      micGain.disconnect();
      micSource?.disconnect();
      analyser.disconnect();
      destination.disconnect();
    },
  };
}

function clamp(level: number): number {
  return Math.min(1, Math.max(0, level));
}
